import { inject } from 'vue'
import {
  ApolloClient,
  createHttpLink,
  type ServerError,
  type ServerParseError,
} from '@apollo/client/core'
import type { NormalizedCacheObject } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import { getAccessToken, getLoginIdentifier } from '/@/utils/cookie'
import { useAuth } from '/@/core/auth'
import { useGenericModal } from '/@/core/modals/GenericModal'
import { reportError } from '/@/core/rollbar'
import type { Config } from '/@/core/config'
import { createCache } from './cache'
import { DefaultApolloClient } from './shims'
import { showSupersededSessionModal } from '/@/core/sessionSuperseded'
import { toQueryString, getVendorParams } from '/@/utils/misc'
import {
  isConnectionError,
  isAbortError,
  isSupersededSessionResponse,
} from '/@/utils/http'
import { useToast } from '../elements/toast'
import { getAnonymousId, _pageLevelData } from '/@/core/tracking/api'
import type { Router } from 'vue-router'

export * from './shims'
export { GraphqlCacheSymbol } from './cache'

export const TRACKING_SEPARATOR = '_'

function isServerError(
  networkError: Error | ServerError | ServerParseError | undefined,
): networkError is ServerError {
  if (!networkError) {
    return false
  } else if ('statusCode' in networkError) {
    return true
  } else {
    return false
  }
}

export function createGraphql(config: Config, router: Router) {
  const token = getAccessToken(config)
  const loginIdentifier = getLoginIdentifier()
  // On localhost the app is cross-site to spoak.com, so the sidt/slid cookies
  // aren't sent — dev keeps authenticating via header. Every deployed env
  // (including non-prod) authenticates via the credentialed cookie instead.
  const isDevAuth = config.mode === 'development' || config.mode === 'e2e'
  const cache = createCache()
  const vendorParams = getVendorParams()

  const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const route = router.currentRoute.value

    let fetchUri = input
    let query = ''
    try {
      const queryString = toQueryString(vendorParams.value)
      if (queryString.length) query = `&${queryString}`
    } catch {
      // noop
    }

    query +=
      '&tracking.site=app&tracking.domain=' +
      encodeURIComponent(window.location.hostname)

    if (route) {
      query += `&tracking.page=${encodeURIComponent(route.meta?.pageName ?? route.name)}`
    }
    if (_pageLevelData.value.viziType) {
      query += `&tracking.vizi_type=${encodeURIComponent(_pageLevelData.value.viziType)}`
    }
    if (init?.body) {
      const { operationName } = JSON.parse(init.body as string)
      const [o, trackingComponent] = operationName.split(TRACKING_SEPARATOR)
      const trackingComponentParam = trackingComponent
        ? `&tracking.component=${trackingComponent}`
        : ''
      fetchUri = `${input}?o=${o}${trackingComponentParam}${query}`
    }

    if (init?.headers) {
      ;(init.headers as Record<string, string>)['Spoak-App-Version'] =
        config.version
      ;(init.headers as Record<string, string>)['Spoak-App'] = 'app'

      const trackingAnonymousId = getAnonymousId()
      if (trackingAnonymousId) {
        ;(init.headers as Record<string, string>)['Spoak-Anonymous-Id'] =
          trackingAnonymousId
      }
    }

    const res = await fetch(fetchUri, init)
    // A replaced session is answered as a guest where guests are allowed, so the header,
    // not the status, is what says this tab was logged out by a login elsewhere.
    if (isSupersededSessionResponse(res.headers)) showSupersededSessionModal()
    return res
  }

  const headers: Record<string, string> = {}
  if (isDevAuth) {
    headers['Authorization'] = token ? `Bearer ${token}` : ''
    if (loginIdentifier) headers['Spoak-Login-Identifier'] = loginIdentifier
  }

  const httpLink = createHttpLink({
    uri: config.urls.gql,
    fetch: customFetch,
    headers,
    credentials: 'include',
  })

  const errorLink = onError(({ operation, networkError, graphQLErrors }) => {
    if (isServerError(networkError)) {
      if (isSupersededSessionResponse(networkError.response?.headers)) {
        // The fetch wrapper has already shown the modal for it.
      } else if (networkError.statusCode === 401) {
        const auth = useAuth()
        const genericModal = useGenericModal()

        genericModal.show({
          title: 'Oops!',
          content:
            'It looks like your session is no longer active. Please log in again to continue.',
          btns: [
            {
              label: 'Log in',
              type: 'primary',
              async action() {
                await auth.logOut()
              },
            },
          ],
          strict: true,
        })
      } else {
        reportError(new Error('Unexpected network error response'), {
          query: operation.query,
          statusCode: networkError.statusCode,
          jsError: networkError.name,
          jsErrorMessage: networkError.message,
          jsErrorStack: networkError.stack,
        })
      }
    } else if (
      networkError &&
      !isConnectionError(networkError) &&
      !isAbortError(networkError)
    ) {
      reportError(new Error('Unexpected network error'), {
        query: operation.query,
        jsError: networkError.name,
        jsErrorMessage: networkError.message,
        jsErrorStack: networkError.stack,
      })
    } else if (graphQLErrors) {
      reportError(new Error('GraphQL error'), {
        query: operation.query,
        graphQLErrors,
      })
    }
  })

  // Create the apollo client
  const client = new ApolloClient({
    link: errorLink.concat(httpLink),
    cache,
  })

  return { client, cache }
}

export function useGraphqlClient() {
  return inject(DefaultApolloClient) as ApolloClient<NormalizedCacheObject>
}

export interface Message {
  text: string
}
export function handleError(messagePrefix: string, resultMessage?: Message) {
  const toast = useToast()
  const errorMessage = `${messagePrefix}${resultMessage?.text ? ` ${resultMessage.text}` : ''}`
  toast.show({
    message: errorMessage,
    type: 'stella',
    severity: 'critical',
  })
}
